import { useState, useEffect, useCallback } from "react";
import {
  Plus, X, Loader2, Trash2, Edit3, FileSpreadsheet, Download,
  Upload, Search, Hammer, CheckSquare, Square, ListTree, Settings2,
} from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { fmt, opts } from "./constants";
import { ImportDesgloseModal } from "./ImportDesgloseModal";
import { JornalesSettingsModal } from "./JornalesSettingsModal";
import { useCatalogoPresupuesto, CatalogoScopeBanner, CatalogoSinGuardar } from "./catalogoScope";

export function ManoObraTab() {
  // Fix CC (2026-09-10): dentro de un presupuesto, su copia; en la Base Madre, la base.
  const { catApi, budgetId, sinGuardar } = useCatalogoPresupuesto();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showDesglose, setShowDesglose] = useState(false);
  const [showJornales, setShowJornales] = useState(false);
  // PDF REV3 #1 — Selección múltiple para eliminación masiva
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${catApi}/bocas-precios/mano-obra`, opts);
      setItems(Array.isArray(data) ? data : []);
    } catch { toast.error("Error al cargar mano de obra"); }
    finally { setLoading(false); }
  }, [catApi]);

  useEffect(() => { if (!sinGuardar) fetchAll(); }, [fetchAll, sinGuardar]);

  // Fix CC (2026-09-10): un presupuesto nuevo todavía no tiene copia: no se edita la base desde acá.
  if (sinGuardar) return <CatalogoSinGuardar que="Los rubros de mano de obra" />;

  const filtered = items.filter(b =>
    !search ||
    (b.descripcion || "").toLowerCase().includes(search.toLowerCase()) ||
    (b.categoria || "").toLowerCase().includes(search.toLowerCase())
  );

  // Group by categoria for nicer display
  const grouped = filtered.reduce((acc, item) => {
    const cat = item.categoria || "General";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar este ítem de mano de obra?")) return;
    try {
      await axios.delete(`${catApi}/bocas-precios/mano-obra/${id}`, opts);
      toast.success("Ítem eliminado");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  // PDF REV3 #1 — Helpers de selección múltiple
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const visibleIds = filtered.map((b) => b._id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = visibleIds.some((id) => selected.has(id));
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allSelected) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    return next;
  });
  const clearSelection = () => setSelected(new Set());

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`¿Eliminar ${selected.size} rubro${selected.size !== 1 ? "s" : ""} de Mano de Obra?\nEsta acción no se puede deshacer.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await axios.post(`${catApi}/bocas-precios/mano-obra/bulk-delete`, { ids: Array.from(selected) }, opts);
      toast.success(`${data.deleted || 0} rubro${(data.deleted || 0) !== 1 ? "s" : ""} eliminado${(data.deleted || 0) !== 1 ? "s" : ""}`);
      clearSelection();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar rubros");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/bocas-precios/mano-obra/template`, "_blank");
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!confirm(`¿Importar "${file.name}"? Esto AGREGARÁ los precios al catálogo actual.`)) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/bocas-precios/mano-obra/import-excel?replace=false`, fd, {
        ...opts, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.imported} ítems de M.O. importados (${data.skipped} omitidos)`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar Excel");
    } finally { setImporting(false); }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
      {budgetId && <CatalogoScopeBanner onRecopiado={fetchAll} />}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <h3 className="text-sm font-bold text-[#1A4B7A] uppercase tracking-wide flex items-center gap-2">
              <Hammer size={14} /> Catálogo de MANO DE OBRA
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Costos unitarios por rubro de mano de obra — usados como sugerencia en C.Unit M.O del presupuesto.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setShowJornales(true)}
              className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
              data-testid="mo-jornales-btn">
              <Settings2 size={13} /> Parámetros de Jornal
            </button>
            {/* Fix CC (2026-09-10): importar planillas y el desglose escriben la BASE
                MADRE: dentro de un presupuesto no se muestran. */}
            {!budgetId && (<>
            <button onClick={handleDownloadTemplate}
              className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
              data-testid="mo-download-template-btn">
              <Download size={13} /> Plantilla Excel
            </button>
            <label className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer">
              {importing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {importing ? "Importando..." : "Importar Excel"}
              <input type="file" accept=".xlsx,.xls" onChange={handleImportExcel} className="hidden" disabled={importing} data-testid="mo-import-excel-input" />
            </label>
            <button onClick={() => setShowDesglose(true)}
              className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
              data-testid="mo-import-desglose-btn">
              <ListTree size={13} /> Importar Desglose (MANO DE OBRA.xlsx)
            </button>
            </>)}

            <button onClick={() => setEditing("new")}
              className="flex items-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
              data-testid="mo-new-btn">
              <Plus size={13} /> Nuevo Rubro
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar rubro o descripción..."
              className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="mo-search-input" />
          </div>
        </div>

        {/* PDF REV3 #1 — Barra de acciones masivas */}
        {selected.size > 0 && (
          <div className="flex items-center gap-3 mb-3 px-4 py-2 bg-red-50 border-2 border-red-200 rounded-xl" data-testid="mo-bulk-action-bar">
            <CheckSquare size={14} className="text-red-600 flex-shrink-0" />
            <span className="text-xs font-bold text-red-700">
              {selected.size} rubro{selected.size !== 1 ? "s" : ""} seleccionado{selected.size !== 1 ? "s" : ""}
            </span>
            <button onClick={clearSelection} className="text-xs font-medium text-red-700 hover:underline">
              Limpiar selección
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="ml-auto flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-60"
              data-testid="mo-bulk-delete-btn"
            >
              {bulkDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              {bulkDeleting ? "Eliminando..." : `Eliminar ${selected.size} ítem${selected.size !== 1 ? "s" : ""}`}
            </button>
          </div>
        )}

        {/* PDF REV3 #1 — Botón Seleccionar todos en una barra global */}
        {!loading && filtered.length > 0 && (
          <div className="flex items-center gap-2 mb-3 text-xs">
            <button
              onClick={toggleAll}
              className="inline-flex items-center gap-1.5 text-[#1A4B7A] hover:text-[#2E6DA4] font-semibold"
              data-testid="mo-select-all-btn"
            >
              {allSelected ? <CheckSquare size={14} /> : someSelected ? <CheckSquare size={14} className="opacity-60" /> : <Square size={14} />}
              {allSelected ? "Deseleccionar todos" : "Seleccionar todos los visibles"}
            </button>
            <span className="text-gray-400">({filtered.length} visible{filtered.length !== 1 ? "s" : ""})</span>
          </div>
        )}

        {loading ? (
          <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10 rounded-lg" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="bg-gray-50 rounded-xl p-12 text-center text-gray-400">
            <Hammer size={32} className="mx-auto mb-2 opacity-30" />
            <p className="font-medium">No hay rubros de mano de obra cargados</p>
            <p className="text-xs mt-1">Importá una planilla Excel o creá manualmente.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0])).map(([cat, list]) => (
              <div key={cat} className="rounded-xl border border-gray-100 overflow-hidden">
                <div className="bg-[#EBF3FB] px-4 py-2 border-b border-blue-100">
                  <p className="text-xs font-bold text-[#1A4B7A] uppercase tracking-wide">{cat} <span className="font-normal text-gray-500">({list.length})</span></p>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="w-10 px-2 py-2 text-center"></th>
                      <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase tracking-wide">Descripción</th>
                      <th className="text-center px-3 py-2 font-bold text-gray-500 uppercase tracking-wide">Horas Ay./Of.</th>
                      <th className="text-right px-3 py-2 font-bold text-gray-500 uppercase tracking-wide">Precio Unit.</th>
                      <th className="text-center px-3 py-2 font-bold text-gray-500 uppercase tracking-wide">Unidad</th>
                      <th className="text-right px-3 py-2 font-bold text-gray-500 uppercase tracking-wide w-24">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map(it => {
                      const isSel = selected.has(it._id);
                      return (
                      <tr
                        key={it._id}
                        className={`border-b border-gray-50 hover:bg-gray-50/60 ${isSel ? "bg-blue-50/40" : ""}`}
                        data-testid={`mo-row-${it._id}`}
                      >
                        <td className="px-2 py-2 text-center">
                          <button
                            onClick={() => toggleOne(it._id)}
                            className="text-[#1A4B7A] hover:text-[#2E6DA4] inline-flex items-center justify-center"
                            data-testid={`mo-select-${it._id}`}
                            aria-label="Seleccionar"
                          >
                            {isSel ? <CheckSquare size={13} /> : <Square size={13} />}
                          </button>
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-800">
                          {it.descripcion}
                          {Array.isArray(it.recursos_extra) && it.recursos_extra.length > 0 && (
                            <span className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-violet-100 text-violet-700">
                              +{it.recursos_extra.map(r => r.nombre).join(", ")}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center text-gray-500 tabular-nums">
                          {(it.horas_ayudante != null || it.horas_oficial != null)
                            ? `${it.horas_ayudante || 0}h / ${it.horas_oficial || 0}h`
                            : <span className="text-gray-300">manual</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-bold tabular-nums text-emerald-700">{fmt(it.precio_unitario)}</td>
                        <td className="px-3 py-2 text-center text-gray-600">{it.unidad}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => setEditing(it)} className="p-1 rounded hover:bg-blue-50 text-blue-600 mr-1" data-testid={`mo-edit-${it._id}`}>
                            <Edit3 size={12} />
                          </button>
                          <button onClick={() => handleDelete(it._id)} className="p-1 rounded hover:bg-red-50 text-red-600" data-testid={`mo-delete-${it._id}`}>
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
          <FileSpreadsheet size={12} /> {filtered.length} rubro{filtered.length !== 1 ? "s" : ""} mostrado{filtered.length !== 1 ? "s" : ""} de {items.length} total{items.length !== 1 ? "es" : ""}.
        </div>
      </div>

      <ManoObraEditModal
        open={!!editing}
        item={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchAll(); }}
      />
      <ImportDesgloseModal
        open={showDesglose}
        type="mano-obra"
        onClose={() => setShowDesglose(false)}
        onImported={() => { setShowDesglose(false); fetchAll(); }}
      />
      <JornalesSettingsModal
        open={showJornales}
        onClose={() => setShowJornales(false)}
        onSaved={() => { setShowJornales(false); fetchAll(); }}
      />
    </div>
  );
}

function ManoObraEditModal({ open, item, onClose, onSaved }) {
  const { catApi } = useCatalogoPresupuesto(); // Fix CC (2026-09-10)
  const [form, setForm] = useState({
    categoria: "", descripcion: "", precio_unitario: 0, unidad: "un",
  });
  const [usarHoras, setUsarHoras] = useState(false);
  const [horasAyudante, setHorasAyudante] = useState(0);
  const [horasOficial, setHorasOficial] = useState(0);
  const [recursos, setRecursos] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(item ? {
        categoria: item.categoria || "",
        descripcion: item.descripcion || "",
        precio_unitario: item.precio_unitario || 0,
        unidad: item.unidad || "un",
      } : {
        categoria: "", descripcion: "", precio_unitario: 0, unidad: "un",
      });
      const tieneHoras = !!item && (item.horas_ayudante != null || item.horas_oficial != null);
      setUsarHoras(tieneHoras);
      setHorasAyudante(item?.horas_ayudante || 0);
      setHorasOficial(item?.horas_oficial || 0);
      setRecursos(Array.isArray(item?.recursos_extra) ? item.recursos_extra.map(r => ({ ...r })) : []);
    }
  }, [open, item]);

  if (!open) return null;

  const addRecurso = () => setRecursos(prev => [...prev, { nombre: "", costo_dia: 0, cantidad: 0 }]);
  const updateRecurso = (idx, field, value) => setRecursos(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const removeRecurso = (idx) => setRecursos(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!form.descripcion.trim()) { toast.error("La descripción es obligatoria"); return; }
    setSaving(true);
    try {
      const payload = {
        categoria: form.categoria.trim() || "General",
        descripcion: form.descripcion.trim(),
        precio_unitario: Number(form.precio_unitario) || 0,
        unidad: form.unidad.trim() || "un",
        horas_ayudante: usarHoras ? (Number(horasAyudante) || 0) : null,
        horas_oficial: usarHoras ? (Number(horasOficial) || 0) : null,
        recursos_extra: usarHoras ? recursos.filter(r => (r.nombre || "").trim()).map(r => ({
          nombre: r.nombre.trim(), costo_dia: Number(r.costo_dia) || 0, cantidad: Number(r.cantidad) || 0,
        })) : null,
      };
      if (item?._id) {
        await axios.put(`${catApi}/bocas-precios/mano-obra/${item._id}`, payload, opts);
      } else {
        await axios.post(`${catApi}/bocas-precios/mano-obra`, payload, opts);
      }
      toast.success(item?._id ? "Rubro actualizado" : "Rubro creado");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="mo-edit-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-gray-900">{item ? "Editar Rubro M.O." : "Nuevo Rubro M.O."}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Categoría</label>
            <input value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}
              placeholder="Ej: Llaves, Bocas, Movimiento de Suelo..."
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="mo-categoria-input" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Descripción *</label>
            <input value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
              placeholder="Ej: Montaje de llave TM Trifásico"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="mo-descripcion-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">
                Precio Unitario (Gs.) {usarHoras && <span className="text-violet-600">— calculado</span>}
              </label>
              <input type="number" min="0" disabled={usarHoras} value={form.precio_unitario} onChange={e => setForm(f => ({ ...f, precio_unitario: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#2E6DA4]/30 disabled:bg-gray-50 disabled:text-violet-700 disabled:font-bold" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Unidad</label>
              <input value={form.unidad} onChange={e => setForm(f => ({ ...f, unidad: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30" />
            </div>
          </div>

          <div className="border border-gray-200 rounded-xl p-3 bg-gray-50/60">
            <label className="flex items-center gap-2 text-xs font-bold text-gray-700 cursor-pointer mb-2">
              <input type="checkbox" checked={usarHoras} onChange={e => setUsarHoras(e.target.checked)} data-testid="mo-usar-horas-checkbox" />
              Derivar precio desde horas de jornal (Ayudante / Oficial)
            </label>
            {usarHoras && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">Horas Ayudante</label>
                    <input type="number" step="0.1" value={horasAyudante} onChange={e => setHorasAyudante(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" data-testid="mo-horas-ayudante-input" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">Horas Oficial</label>
                    <input type="number" step="0.1" value={horasOficial} onChange={e => setHorasOficial(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" data-testid="mo-horas-oficial-input" />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-semibold text-gray-600">Recursos extra (ej. Andamio, Elevador)</span>
                    <button type="button" onClick={addRecurso} className="text-[11px] font-semibold text-[#2E6DA4] hover:text-[#1A4B7A] flex items-center gap-1">
                      <Plus size={11} /> Agregar
                    </button>
                  </div>
                  {recursos.map((r, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 mb-1">
                      <input value={r.nombre} onChange={e => updateRecurso(idx, "nombre", e.target.value)} placeholder="Nombre"
                        className="flex-1 border border-gray-200 rounded-lg px-2 py-1 text-[11px]" />
                      <input type="number" value={r.costo_dia} onChange={e => updateRecurso(idx, "costo_dia", e.target.value)} placeholder="Costo/día"
                        className="w-24 border border-gray-200 rounded-lg px-2 py-1 text-[11px] text-right" />
                      <input type="number" step="0.1" value={r.cantidad} onChange={e => updateRecurso(idx, "cantidad", e.target.value)} placeholder="Horas"
                        className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-[11px] text-right" />
                      <button type="button" onClick={() => removeRecurso(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 size={12} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
            data-testid="mo-save-btn">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
            {saving ? "Guardando..." : (item ? "Guardar" : "Crear")}
          </button>
        </div>
      </div>
    </div>
  );
}
